/**
 * Anonymous, provider-agnostic product-analytics seam for DESK.
 *
 * Design (non-negotiable — see .superpowers/sdd/sticker/r16-brief.md):
 *  - NO-OP BY DEFAULT: `track()` does nothing unless analytics is explicitly
 *    configured AND enabled (prod build + both Umami env vars present + not
 *    opted out). Dev, tests, and unconfigured deploys make ZERO network calls.
 *  - ANONYMOUS: no login, no PII, no generated user id. Event props carry only
 *    game state (level id, crowd id, sticker id, style id, pack/item id) —
 *    never anything identifying. Umami itself is cookieless by default.
 *  - FAIL-SAFE: sink calls are wrapped in try/catch; a missing/blocked script
 *    is a silent no-op and never throws into the game.
 *  - ONE THIN SEAM: the game only ever calls `track(event)` — the provider
 *    (Umami) lives entirely behind this module, so swapping/adding a provider
 *    later is a one-file change.
 *
 * This module is presentation/telemetry ONLY: it must never affect scoring,
 * gameplay, determinism, or the sim.
 */

/** The full set of product events DESK emits, with their exact prop shapes. */
export type AnalyticsEvent =
  | { name: "level_opened"; props: { levelId: string } }
  | { name: "level_published"; props: { levelId: string } }
  | { name: "level_result"; props: { levelId: string; crowdId: string; landed: boolean } }
  | { name: "sticker_placed"; props: { stickerId: string; onZone: boolean } }
  | { name: "caption_style_changed"; props: { style: string } }
  | { name: "store_item_purchased"; props: { itemId: string } }
  | { name: "about_opened"; props?: Record<string, never> };

/** localStorage flag the (future) settings toggle reads/writes. */
const OPT_OUT_KEY = "desk.analytics.optout";

/**
 * localStorage marker for `level_result` dedup: the identity (see
 * `publishResultIdentity`) of the most recently reported publish. Survives
 * remounts/reloads so restoring straight onto the Compare screen (autosave
 * persists `screen:"compare"` + the unconsumed `lastPublish` — see
 * `save.ts`) never re-reports the same publish's results.
 */
const LAST_RESULT_REPORTED_KEY = "desk.analytics.lastResultReported";

/** Minimal Umami global typing — the script itself attaches this; no dependency added. */
declare global {
  interface Window {
    umami?: {
      track: (eventName: string, data?: Record<string, unknown>) => void;
    };
  }
}

let scriptInjected = false;

/**
 * Test-only override for `isEnabled()`, so tests can exercise the "enabled"
 * path without a real prod build / env vars. Never read by anything except
 * `isEnabled()` below; production code never calls this setter.
 */
let testEnabledOverride: boolean | null = null;
export function __setAnalyticsEnabledOverrideForTests(value: boolean | null): void {
  testEnabledOverride = value;
}
/** Test-only reset of the "script already injected" guard. */
export function __resetAnalyticsInjectedForTests(): void {
  scriptInjected = false;
}

function hasLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

/** Whether the player has opted out via the (future) settings toggle. Default: not opted out. */
export function optedOut(): boolean {
  if (!hasLocalStorage()) return false;
  try {
    return window.localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Sets/clears the opt-out flag for a future settings toggle. Never throws. */
export function setAnalyticsOptOut(optOut: boolean): void {
  if (!hasLocalStorage()) return;
  try {
    if (optOut) window.localStorage.setItem(OPT_OUT_KEY, "1");
    else window.localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* storage unavailable (private mode); opt-out just won't persist */
  }
}

function envConfigured(): boolean {
  const env = import.meta.env;
  return Boolean(env.PROD) && Boolean(env.VITE_UMAMI_SRC) && Boolean(env.VITE_UMAMI_WEBSITE_ID);
}

/**
 * Whether analytics is explicitly configured AND enabled right now:
 * a PROD build, both Umami env vars present, and the player has not opted
 * out. In dev/test builds (or with either env var missing) this is always
 * false, so `track()`/`initAnalytics()` are no-ops and no network call is
 * ever made.
 */
export function isEnabled(): boolean {
  const configured = testEnabledOverride !== null ? testEnabledOverride : envConfigured();
  return configured && !optedOut();
}

/**
 * Fire-and-forget event tracking. NO-OP unless `isEnabled()` — the single
 * gate that keeps dev/tests/unconfigured deploys offline. Never throws: any
 * sink error (missing/blocked script, ad blocker, etc.) is swallowed.
 */
export function track(event: AnalyticsEvent): void {
  if (!isEnabled()) return;
  try {
    window.umami?.track(event.name, event.props);
  } catch {
    /* sink error — analytics must never break the game */
  }
}

/** Stable (non-cryptographic) FNV-1a hash — deterministic, no Math.random/Date.now. */
function stableHash(s: string): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

/**
 * Stable identity for a publish's `level_result` batch, so it can be reported
 * exactly once even across remounts/reloads: `levelId` + a hash of the
 * serialized published edit graph (the brief's suggested derivation — no
 * publish id exists on `PublishSnapshot` today). Presentation-only; not a
 * gameplay hash and never fed back into scoring.
 */
export function publishResultIdentity(levelId: string, graphJson: string): string {
  return `${levelId}:${stableHash(graphJson)}`;
}

function alreadyReportedResult(identity: string): boolean {
  if (!hasLocalStorage()) return false;
  try {
    return window.localStorage.getItem(LAST_RESULT_REPORTED_KEY) === identity;
  } catch {
    return false;
  }
}

function markResultReported(identity: string): void {
  if (!hasLocalStorage()) return;
  try {
    window.localStorage.setItem(LAST_RESULT_REPORTED_KEY, identity);
  } catch {
    /* storage unavailable (private mode); dedupe just won't persist across mounts */
  }
}

/**
 * Fires `track({ name: "level_result", props })` exactly once per publish
 * `identity` (see `publishResultIdentity`), surviving remounts: a
 * reload/resume that restores straight onto the Compare screen for the SAME
 * publish reports nothing (the identity was already marked reported); a NEW
 * publish (different identity) reports again. Fully no-op — including no
 * storage read/write — when analytics is disabled, and fail-safe throughout
 * (a storage error is treated as "not yet reported" so a genuine first
 * publish is never silently dropped).
 */
export function trackLevelResultOnce(
  identity: string,
  props: { levelId: string; crowdId: string; landed: boolean },
): void {
  if (!isEnabled()) return;
  if (alreadyReportedResult(identity)) return;
  track({ name: "level_result", props });
  markResultReported(identity);
}

/** Test-only reset of the `level_result` dedup marker. */
export function __resetAnalyticsLastResultReportedForTests(): void {
  if (!hasLocalStorage()) return;
  try {
    window.localStorage.removeItem(LAST_RESULT_REPORTED_KEY);
  } catch {
    /* storage unavailable; nothing to reset */
  }
}

/**
 * Injects the Umami script tag once, only when analytics is enabled. Safe to
 * call unconditionally at app startup — it self-guards on every axis: skips
 * if already injected, if either env var is missing, if not a PROD build
 * (covers dev + test), or if the player opted out.
 */
export function initAnalytics(): void {
  if (scriptInjected) return;
  if (!isEnabled()) return;
  if (typeof document === "undefined") return;
  const src = import.meta.env.VITE_UMAMI_SRC;
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;
  if (!src || !websiteId) return;
  try {
    const script = document.createElement("script");
    script.defer = true;
    script.src = src;
    script.setAttribute("data-website-id", websiteId);
    document.head.appendChild(script);
    scriptInjected = true;
  } catch {
    /* injection failed (e.g. no document.head) — stay a no-op */
  }
}
